/* eslint-disable no-restricted-globals, no-shadow */

import { createMuiTheme } from '@material-ui/core/styles'
import { darken, fade } from '@material-ui/core/styles/colorManipulator'

const colors = {
  border: '#99A8B6',
  white: '#ffffff',
  offWhite: '#F3F7FB',
  lightGrey: '#EAF0F5',
  midGrey: '#D5DEE7',
  darkGrey: '#8E9BAD',
  darkestGrey: '#4D5A6A',
  black: '#222222',
  teal: '#3FC0C3',
  tealWithText: '#33A1A3',
  navy: '#0D1330',
  slate: '#3E547D',
  blue: '#2A4B9B',
  lightBlue: '#4AC4F0',
  green: '#26B861',
  lightGreen: '#ECFBF6',
  red: '#CA3D5F',
  lightRed: '#F4E2E6',
  yellow: '#F4C175',
  lightYellow: '#FFEACB',
  orange: '#FF9A39',
  lightOrange: '#ffa000',
  purple: '#4A34D4',
  blueNew: '#2362FC',
  charcoal: '#4a4a4a',
  darkModeBlack: '#000000',
  darkModeOffBlack: '#121212',
  darkModeHighBlack: '#181818',
  darkModeMidBlack: '#1E1E1E',
  darkModeLowishBlack: '#272727',
  darkModeLowBlack: '#2C2C2C',
  darkModeOffNavy: '#1B2349',
  darkModeBorder: '#424853',
  darkModeExtraHighGrey: '#6E7581',
  darkModeGrey: '#B3BFD1',
  darkModeLowGrey: '#C4D0E5',
  darkModeOffWhite: '#F9F9F9',
  darkModeSlate: '#3E547D',
  darkModeLightGrey: '#AFBDCA',
  darkModeGreen: '#26B861',
  darkModeTeal: '#2f9293',
  darkModeMidGrey: '#2f2f2f',
  darkModeHighGrey: '#404040',
}

const baseTheme = {
  palette: {
    primary: {
      main: colors.teal,
      dark: darken(colors.teal, 0.3),
      contrastText: colors.white,
    },
    secondary: {
      main: colors.darkGrey,
      dark: darken(colors.darkGrey, 0.3),
    },
  },
  colors,
  body: {
    background: colors.offWhite,
    backgroundAlt: colors.white,
    textColor: colors.slate,
    textColorAlt: colors.darkestGrey,
    textColorDark: colors.navy,
    textColorLight: colors.darkGrey,
    textColorExtraLight: colors.border,
    headingColor: colors.navy,
    headingBorderColor: colors.border,
  },
  header: {
    textColor: colors.white,
    background: colors.blue,
  },
  mainNav: {
    expandedWidth: 250,
    collapsedWidth: 46,
    topBarHeightSmall: 50,
    topBarHeightLarge: 64,
    previousOrgHeight: 30,
    sidebarBgColor: colors.white,
    topBarBgColor: colors.white,
    topBarTextColor: colors.slate,
    topBarSeperatorColor: colors.offWhite,
    topBarBorderColor: colors.midGrey,
    topBarIconColor: colors.darkGrey,
    topBarIconColorHover: colors.blue,
    topBarIconColorHoverDarkMode: colors.white,
    transition: '0.6s all cubic-bezier(.28, .28, 0, 1)',
    toggleColor: colors.midGrey,
    toggleColorHover: colors.blue,
    topOrgUserBorderColor: colors.midGrey,
    avatarColor: colors.blue,
    breakpoint: 960,
    iconColor: colors.slate,
    logoColor: colors.blue,
  },
  anchor: {
    color: colors.teal,
    hoverColor: darken(colors.teal, 0.3),
    textDecoration: 'none',
  },
  buttons: {
    primaryButtonColor: colors.white,
    primaryButtonBg: colors.tealWithText,
    primaryButtonBgHover: darken(colors.teal, 0.3),
    lightButtonBgHover: fade(colors.white, 0.3),
    deleteButtonColor: colors.white,
    deleteButtonBg: colors.red,
    deleteButtonBgHover: darken(colors.red, 0.3),
    actionButtonColor: colors.darkestGrey,
    actionButtonColorDisabled: colors.border,
    actionButtonBg: colors.lightGrey,
    actionButtonBgHover: colors.midGrey,
    actionButtonBgDisabled: colors.lightGrey,
    actionButtonBgDisabledParent: colors.white,
    actionButtonBgRowRender: colors.white,
    actionButtonBorderDisabledParent: colors.border,
    backButtonColor: colors.darkestGrey,
    backButtonBg: colors.lightGrey,
    backButtonBgHover: colors.midGrey,
    backButtonBgHoverDarkMode: colors.darkModeSlate,
    iconColor: colors.darkGrey,
    iconDisabledColor: colors.darkGrey,
    marketplaceButtonColor: colors.white,
    marketplaceButtonBg: colors.purple,
    marketplaceButtonBgHover: darken(colors.purple, 0.3),
    darkModeButtonColor: colors.navy,
    darkModeButtonBg: colors.offWhite,
  },
  borders: {
    default: `1px solid var(--gameday-borders-borderColor, ${colors.border})`,
    dashed: `1px dashed var(--gameday-borders-borderColor, ${colors.border})`,
    borderRadius: '4px',
    borderColor: colors.border,
    borderActive: `1px solid ${colors.teal}`,
    borderColorActive: colors.teal,
    borderColorError: colors.red,
    borderColorDark: colors.midGrey,
  },
  status: {
    alertMini: colors.slate,
    alertMiniBG: colors.white,
    infoBG: colors.lightGrey,
    infoBorder: colors.border,
    infoColor: colors.blue,
    infoIcon: colors.blue,
    infoHover: darken(colors.lightGrey, 0.05),
    successBG: colors.lightGreen,
    successBorder: colors.green,
    successColor: colors.green,
    successIcon: colors.green,
    successHover: darken(colors.lightGreen, 0.05),
    warningBG: colors.lightYellow,
    warningBorder: colors.yellow,
    warningColor: colors.orange,
    warningColorAlert: colors.navy,
    warningIcon: colors.orange,
    warningIconAlert: colors.navy,
    warningHover: darken(colors.lightYellow, 0.05),
    errorBG: colors.lightRed,
    errorBorder: colors.red,
    errorColor: colors.red,
    errorIcon: colors.red,
    errorHover: darken(colors.lightRed, 0.05),
    disabledColor: colors.darkGrey,
    disabledIcon: colors.darkGrey,
    disabledBorder: colors.border,
    timerBG: colors.lightYellow,
    timerColor: colors.orange,
    requiredColor: colors.red,
    errorBGDarkMode: colors.red,
    errorColorDarkMode: colors.white,
    infoBGDarkMode: colors.darkModeLightGrey,
    errorIconDarkMode: colors.white,
    infoColorDarkMode: colors.navy,
    infoIconDarkMode: colors.navy,
    successBGDarkMode: colors.darkModeGreen,
    successColorDarkMode: colors.white,
    successIconDarkMode: colors.white,
    warningBGDarkMode: colors.yellow,
  },
  statusLabel: {
    color: {
      default: colors.white,
      white: colors.blue,
      info: colors.slate,
    },
    bgColor: {
      default: colors.blue,
      success: colors.green,
      error: colors.red,
      warning: colors.lightOrange,
      info: colors.lightGrey,
      black: colors.black,
      white: colors.white,
      new: colors.blueNew,
    },
  },
  accordion: {
    headingColor: colors.slate,
    headingFontSize: '1rem',
    headingFontWeight: '500',
    subHeadingColor: colors.darkestGrey,
    subHeadingFontSize: '0.875rem',
    borderColor: colors.border,
    disabledBg: colors.lightGrey,
  },
  dropdown: {
    zindexDropdown: '1000',
    dropdownMinWidth: '10rem',
    dropdownPaddingY: '.5rem',
    dropdownSpacer: '0',
    dropdownBg: colors.white,
    dropdownBorderWidth: '1px',
    dropdownBorderColor: colors.border,
    dropdownBorderRadius: '0 0 .25rem .25rem',
    dropdownBoxShadow: `0 .5rem 1rem 0 ${colors.midGrey}`,
    dropdownDividerBg: colors.border,
    dropdownItemPaddingX: '1.5rem',
    dropdownItemPaddingY: '0.25rem',
    dropdownLinkColor: colors.black,
    dropdownLinkHoverColor: colors.black,
    dropdownLinkActiveColor: colors.white,
    dropdownLinkHoverBg: colors.lightGrey,
    dropdownLinkDisabledColor: colors.darkGrey,
    dropdownHeaderColor: colors.darkestGrey,
    dropdownTextColor: colors.slate,
  },
  newDropdown: {
    bgColor: colors.white,
    textColor: colors.slate,
    textColorAlt: colors.blue,
    textColorDark: colors.slate,
    textColorActive: colors.navy,
    borderColor: colors.midGrey,
    borderRadius: '4px',
    shadowColor: fade(colors.navy, 0.1),
    buttonBgColor: colors.white,
    buttonBgColorHover: colors.lightGrey,
    buttonBgColorProminent: colors.lightGrey,
    buttonBgColorProminentHover: colors.midGrey,
  },
  error: {
    background: colors.lightRed,
    borderColor: colors.red,
    textColor: colors.red,
  },
  inputField: {
    inputBGColor: colors.white,
    inputBGError: colors.lightRed,
    inputBGHover: colors.lightGrey,
    inputBGDisabled: colors.lightGrey,
    inputBorder: colors.border,
    inputBorderActive: colors.teal,
    inputBorderDisabled: colors.border,
    inputTextColor: colors.slate,
    inputTextColorDisabled: colors.darkGrey,
    inputPlaceholderColor: fade(colors.darkGrey, 0.7),
    inputLabelColor: colors.darkGrey,
    inputSearchBG: colors.offWhite,
    inputSearchBGActive: colors.white,
    inputDisabledOpacity: 1,
    iconColor: colors.darkGrey,
    richTextBg: colors.white,
    countrySelectBg: colors.white,
    datePickerCalendarDayBg: colors.white,
    datePickerCalendarDayBgHover: colors.lightGrey,
    colorPickerBg: colors.lightGrey,
    memberTypeBg: colors.lightGrey,
    htmlWrapperBg: colors.lightGrey,
  },
  navBar: {
    navDropdownPaddingY: '1.25rem',
    navDropdownPaddingX: '.625rem',
    navDropdownHoverBg: colors.lightGrey,
    navDropdownItemPaddingY: '.25rem',
    navDropdownItemPaddingX: '.625rem',
    dropdownPaddingY: '.5rem',
    navTooltipBg: colors.navy,
  },
  paging: {
    textColor: colors.darkestGrey,
    rowsColor: colors.darkestGrey,
    rowsSelectColor: colors.slate,
    bgColor: colors.offWhite,
    hoverBgColor: colors.midGrey,
    activeBorderColor: colors.teal,
    activeColor: colors.white,
    activeBgColor: colors.teal,
    disabledColor: colors.border,
    csvExportColor: colors.darkGrey,
  },
  paper: {
    paperPadding: '1rem',
  },
  rowAction: {
    menuBgColor: colors.lightGrey,
    menuItemBgHover: colors.midGrey,
  },
  spacing: factor => `${0.25 * factor}rem`,
  stepper: {
    stepperBackground: colors.blue,
    stepperForeground: colors.white,
  },
  table: {
    tableBgColor: colors.offWhite,
    tableBorderColor: colors.border,
    tableLinkColor: colors.teal,
    tableHeaderTextColor: colors.darkestGrey,
    tableHeaderBgColor: colors.offWhite,
    tableTextColor: colors.slate,
    tableRowHoverColor: colors.lightGrey,
    tableHeaderRowBgColor: colors.lightGrey,
    tableRowChildBgColor: colors.white,
    tableHeaderLockedBgColor: colors.midGrey,
    tableRowLockedBgColor: fade(colors.offWhite, 0.5),
    tableRowDeletedBgColor: fade(colors.red, 0.2),
    rowDragBg: colors.white,
    orderItemDetailBg: colors.white,
    orderItemDetailTableBg: colors.lightGrey,
    tablePreviewBgColor: colors.white,
    tablePreviewBorderColor: colors.lightGrey,
    tablePreviewColumns: colors.lightGrey,
    tablePreviewOutline: colors.teal,
  },
  tabs: {
    tabBorderColor: colors.border,
    tabActiveColor: colors.blue,
    tabInActiveColor: colors.darkestGrey,
    tabBackgroundColor: colors.lightGrey,
    tabBackgroundHoverColor: colors.midGrey,
    tabBackgroundErrorColor: colors.lightRed,
    tabActiveBackgroundColor: colors.white,
    tabSideHeadingColor: colors.navy,
    tabSideActiveColor: colors.navy,
    tabSideActiveBackgroundColor: colors.offWhite,
    tabSideInActiveColor: colors.darkestGrey,
    toggleGroupDisabledBackgroundColor: colors.lightGrey,
    toggleGroupDisabledBackgroundColorDarkMode: colors.darkModeLightGrey,
    tabNavigatorNumberColor: colors.white,
  },
  questions: {
    questionBg: colors.white,
    questionDisabledBg: colors.lightGrey,
    questionBorderColor: colors.border,
    fieldLabelColor: colors.darkestGrey,
    dragHandleColor: colors.darkGrey,
    questionAdvancedBg: colors.lightGrey,
    questionsAndLayoutsBg: colors.white,
  },
  pageOverlay: {
    background: colors.offWhite,
    transition: '0.3s all ease',
    activeOpacity: 0.75,
  },
  typography: {
    fontSizeBase: '1rem', // Assumes the browser default, typically `16px`
    fontSizeXLg: '2rem',
    fontSizeSmallerMed: '1.125rem',
    fontSizeLg: '1.5rem',
    fontSizeLgish: '1.375rem',
    fontSizeMed: '1.25rem',
    fontSizeMedish: '1.125rem',
    fontSizeSm: '.875rem',
    fontSizeSmaller: '.8125rem',
    fontSizeTinyish: '0.75rem',
    fontSizeTiny: '.6875rem',
    fontSizeMicro: '.625rem',
    fontWeightNormal: '400',
    fontWeightBolder: '500',
    fontWeightBold: '600',
    fontWeightXBold: '700',
    defaultFont: 'Barlow, sans-serif',
  },
  eventsLandingPage: {
    buttonColor: colors.white,
    buttonBgColor: colors.blue,
    gradientPrimaryColor: colors.teal,
    gradientSecondaryColor: colors.blue,
  },
  transitions: {
    short: '0.15s all ease',
    smooth: '0.6s all cubic-bezier(.28, .28, 0, 1)',
  },
  focusRings: {
    shadow: options => {
      const {
        inset = false,
        color = theme === 'light' ? colors.border : colors.darkModeBorder,
        opacity = 0.5,
        width = 3,
      } = options || {}
      return `${inset ? 'inset ' : ''}0 0 0 ${width}px ${fade(color, opacity)}`
    },
  },
  regoForm: {
    pageHeaderHeightMobile: 60,
    pageHeaderHeightDesktop: 70,
    pageHeaderBg: colors.white,
    pageHeaderColor: colors.navy,
    pageHeaderColorLight: colors.darkGrey,
    summaryItemBgLocked: colors.offWhite,
    voucherCodeBg: colors.lightGrey,
    previewBg: colors.offWhite,
    settingsBg: colors.white,
  },
  muiStyles: {
    listBg: colors.white,
    listItem: colors.slate,
    listItemHoverBg: colors.midGrey,
    checkboxBg: colors.white,
    inputPrefix: colors.lightGrey,
  },
  miscComponents: {
    placeholderAvatarImageBg: colors.lightGrey,
    imagePlaceholderBg: colors.white,
    exampleIDModalText: colors.slate,
    dockingFooterBg: colors.slate,
  },
  get overrides() {
    return {
      MuiButton: {
        root: {
          fontFamily: this.typography.defaultFont,
          fontSize: 13,
          fontWeight: this.typography.fontWeightBolder,
        },
        outlined: {
          textTransform: 'none',
        },
        contained: {
          textTransform: 'none',
          border: 'none',
          boxShadow: 'none',
          '&:hover, &:active': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          color: this.buttons.primaryButtonColor,
          backgroundColor: this.buttons.primaryButtonBg,
          '&:disabled': {
            color: this.buttons.primaryButtonColor,
            backgroundColor: this.buttons.primaryButtonBg,
            opacity: 0.5,
          },
        },
        text: {
          textTransform: 'none',
        },
      },
      MuiIconButton: {
        root: {
          color: this.buttons.iconColor,
          '&$disabled': {
            color: this.buttons.iconDisabledColor,
          },
        },
      },
      MuiSelect: {
        icon: {
          color: this.inputField.iconColor,
        },
      },
      MuiStepIcon: {
        root: {
          fontSize: '1.5rem',
          border: `1px solid ${colors.white}`,
          borderRadius: '16px',
          '&$completed': {
            color: colors.white,
            borderColor: colors.navy,
          },
          '&$active': {
            color: colors.white,
            borderColor: colors.navy,
            '& > text': {
              fill: colors.navy,
            },
          },
        },
      },
      MuiList: {
        root: {
          backgroundColor: `var(--gameday-muiStyles-listBg, ${this.muiStyles.listBg})`,
        },
      },
      MuiListItem: {
        root: {
          '&$disabled': {
            opacity: 1,
            color: this.inputField.inputTextColorDisabled,
            borderColor: this.inputField.inputBorderDisabled,
          },
        },
        button: {
          color: `var(--gameday-muiStyles-listItem, ${this.muiStyles.listItem})`,
          '&.Mui-selected': {
            backgroundColor: `var(--gameday-muiStyles-listItemHoverBg, ${this.muiStyles.listItemHoverBg})`,
            '&:hover': {
              backgroundColor: `var(--gameday-muiStyles-listItemHoverBg, ${this.muiStyles.listItemHoverBg})`,
            },
          },
          '&:hover': {
            backgroundColor: `var(--gameday-muiStyles-listItemHoverBg, ${this.muiStyles.listItemHoverBg})`,
          },
        },
      },
      MuiPaper: {
        root: {
          backgroundColor: `var(--gameday-body-backgroundAlt, ${this.body.backgroundAlt})`,
          color: `var(--gameday-body-textColor, ${this.body.textColor})`,
        },
      },
      MuiToggleButton: {
        root: {
          borderColor: colors.border,
        },
      },
      MuiRadio: {
        root: {
          color: colors.darkGrey,
        },
        colorPrimary: {
          '&$disabled': {
            color: this.inputField.inputBorderDisabled,
          },
        },
      },
      MuiTypography: {
        body1: {
          fontFamily: this.typography.defaultFont,
          fontSize: 14,
        },
        body2: {
          fontFamily: this.typography.defaultFont,
        },
      },
      MuiInputBase: {
        root: {
          fontFamily: this.typography.defaultFont,
          overflow: 'hidden',
        },
        input: {
          backgroundColor:
            themeMode === 'light'
              ? undefined
              : `var(--gameday-inputField-inputBGColor, ${this.inputField.inputBGColor})`,
        },
        adornedStart: {
          backgroundColor:
            themeMode === 'light'
              ? undefined
              : `var(--gameday-inputField-inputBGColor, ${this.inputField.inputBGColor})`,
          borderRadius: '4px',
          '& .prefix': {
            backgroundColor: `var(--gameday-muiStyles-inputPrefix, ${this.muiStyles.inputPrefix}) !important`,
            color: `var(--gameday-body-textColor, ${this.body.textColor}) !important`,
          },
        },
        adornedEnd: {
          backgroundColor:
            themeMode === 'light'
              ? undefined
              : `var(--gameday-inputField-inputBGColor, ${this.inputField.inputBGColor})`,
          borderRadius: '3px',
          '& .suffix': {
            backgroundColor: `var(--gameday-muiStyles-inputPrefix, ${this.muiStyles.inputPrefix}) !important`,
            color: `var(--gameday-body-textColor, ${this.body.textColor}) !important`,
          },
        },
      },
      MuiMenuItem: {
        root: {
          fontFamily: this.typography.defaultFont,
        },
      },
      MuiFormLabel: {
        root: {
          color: `var(--gameday-body-textColor, ${this.body.textColor}) !important`,
          fontFamily: this.typography.defaultFont,
          fontWeight: this.typography.fontWeightBolder,
        },
      },
      MuiDialogContentText: {
        root: {
          color: `var(--gameday-body-textColor, ${this.body.textColor})`,
        },
      },
    }
  },
  theme: 'customTheme',
}

const darkModeTheme = {
  body: {
    background: colors.darkModeOffBlack,
    backgroundAlt: colors.darkModeOffBlack,
    textColor: colors.darkModeGrey,
    textColorAlt: colors.darkModeHighGrey,
    textColorDark: colors.darkModeLowGrey,
    textColorLight: colors.darkModeExtraHighGrey,
    headingColor: colors.darkModeOffWhite,
  },
  mainNav: {
    sidebarBgColor: colors.darkModeMidBlack,
    topBarBgColor: colors.darkModeMidBlack,
    topBarSeperatorColor: colors.darkModeBorder,
    topBarBorderColor: colors.darkModeBorder,
    topBarIconColor: colors.darkModeGrey,
    toggleColor: colors.darkModeBorder,
    toggleColorHover: colors.white,
    topOrgUserBorderColor: colors.darkModeBorder,
    iconColor: colors.midGrey,
    logoColor: colors.white,
  },
  buttons: {
    actionButtonColor: colors.darkModeGrey,
    actionButtonColorDisabled: colors.darkModeExtraHighGrey,
    actionButtonBg: colors.darkModeHighBlack,
    actionButtonBgHover: colors.darkModeLowBlack,
    actionButtonBgDisabled: colors.darkModeLowBlack,
    actionButtonBgDisabledParent: colors.darkModeHighBlack,
    actionButtonBgRowRender: colors.darkModeHighBlack,
    backButtonColor: colors.darkModeGrey,
    backButtonBg: colors.darkModeHighBlack,
    backButtonBgHover: colors.darkModeSlate,
    backButtonColorHover: colors.midGrey,
    iconColor: colors.darkModeGrey,
    darkModeButtonColor: colors.darkModeOffWhite,
    darkModeButtonBg: colors.darkModeOffBlack,
  },
  borders: {
    borderColor: colors.darkModeBorder,
  },
  status: {
    alertMini: colors.darkModeOffWhite,
    alertMiniBG: colors.darkModeLowBlack,
  },
  newDropdown: {
    bgColor: colors.darkModeMidBlack,
    textColor: colors.darkModeLowGrey,
    textColorAlt: colors.white,
    textColorActive: colors.darkModeOffWhite,
    borderColor: colors.darkModeBorder,
    buttonBgColor: colors.darkModeMidBlack,
    buttonBgColorHover: colors.darkModeOffBlack,
    buttonBgColorProminent: colors.darkModeOffBlack,
    buttonBgColorProminentHover: colors.teal,
  },
  inputField: {
    inputBGColor: colors.darkModeMidBlack,
    inputBGError: colors.darkModeMidBlack,
    inputBGHover: colors.darkModeLowBlack,
    inputBGDisabled: colors.darkModeLowBlack,
    inputBorder: colors.darkModeBorder,
    inputBorderDisabled: colors.darkModeLowBlack,
    inputTextColor: colors.darkModeOffWhite,
    inputTextColorDisabled: colors.darkModeExtraHighGrey,
    inputPlaceholderColor: colors.darkModeLowGrey,
    inputLabelColor: colors.darkModeLowGrey,
    inputSearchBG: colors.darkModeLowBlack,
    inputSearchBGActive: colors.darkModeLowBlack,
    richTextBg: colors.darkModeMidBlack,
    countrySelectBg: colors.darkModeMidBlack,
    datePickerCalendarDayBg: colors.darkModeHighBlack,
    datePickerCalendarDayBgHover: colors.teal,
    colorPickerBg: colors.darkModeMidBlack,
    memberTypeBg: colors.darkModeMidBlack,
    htmlWrapperBg: colors.darkModeMidBlack,
  },
  navBar: {
    navTooltipBg: colors.darkModeLowBlack,
  },
  paging: {
    textColor: colors.darkModeGrey,
    rowsColor: colors.darkModeGrey,
    rowsSelectColor: colors.darkModeGrey,
    bgColor: colors.darkModeOffBlack,
    hoverBgColor: colors.darkModeLowBlack,
    disabledColor: colors.darkModeGrey,
    csvExportColor: colors.darkModeGrey,
  },
  table: {
    tableBgColor: colors.darkModeMidBlack,
    tableBorderColor: colors.darkModeBorder,
    tableHeaderTextColor: colors.darkModeOffWhite,
    tableHeaderBgColor: colors.darkModeHighBlack,
    tableTextColor: colors.darkModeOffWhite,
    tableHeaderRowBgColor: colors.darkModeMidBlack,
    tableRowChildBgColor: colors.darkModeLowishBlack,
    tableRowLockedBgColor: colors.darkModeLowBlack,
    rowDragBg: colors.darkModeLowBlack,
    orderItemDetailBg: colors.darkModeOffBlack,
    orderItemDetailTableBg: colors.darkModeMidBlack,
    tablePreviewBgColor: colors.darkModeMidGrey,
    tablePreviewBorderColor: colors.darkModeBorder,
    tablePreviewColumns: colors.darkModeHighGrey,
    tablePreviewOutline: colors.darkModeTeal,
  },
  tabs: {
    tabActiveColor: colors.darkModeOffWhite,
    tabInActiveColor: colors.darkModeGrey,
    tabBackgroundColor: colors.darkModeHighBlack,
    tabBackgroundHoverColor: colors.darkModeLowBlack,
    tabBackgroundErrorColor: colors.darkModeMidBlack,
    tabActiveBackgroundColor: colors.darkModeMidBlack,
    tabSideHeadingColor: colors.darkModeOffWhite,
    tabSideActiveColor: colors.darkModeOffWhite,
    tabSideActiveBackgroundColor: colors.darkModeLowBlack,
    tabSideInActiveColor: colors.darkModeLowGrey,
    toggleGroupDisabledBackgroundColor: colors.darkModeBorder,
    tabNavigatorNumberColor: colors.darkModeOffBlack,
  },
  questions: {
    questionBg: colors.darkModeHighBlack,
    questionDisabledBg: colors.darkModeLowBlack,
    questionBorderColor: colors.darkModeBorder,
    fieldLabelColor: colors.darkModeLowGrey,
    dragHandleColor: colors.darkModeLowBlack,
    questionAdvancedBg: colors.darkModeMidBlack,
    questionsAndLayoutsBg: colors.darkModeMidBlack,
  },
  regoForm: {
    previewBg: colors.darkModeLowBlack,
    settingsBg: colors.darkModeMidBlack,
  },
  muiStyles: {
    listBg: colors.darkModeLowBlack,
    listItem: colors.darkModeGrey,
    listItemHoverBg: colors.darkModeHighBlack,
    checkboxBg: colors.darkModeOffBlack,
    inputPrefix: colors.darkModeHighBlack,
  },
  miscComponents: {
    placeholderAvatarImageBg: colors.darkModeLowBlack,
    imagePlaceholderBg: colors.darkModeHighBlack,
    exampleIDModalText: colors.darkModeOffWhite,
    dockingFooterBg: colors.darkModeLowBlack,
  },
}
// /* eslint-disable prefer-destructuring */
let themeMode
// /* eslint-disable import/no-mutable-exports */
let theme = createTheme()

function createTheme(theme) {
  themeMode = theme
  return createMuiTheme(baseTheme)
}

function toggleDarkMode(themeMode) {
  if (!themeMode) return
  theme = createTheme(themeMode)

  const rootElement = document.querySelector(':root')
  const exclusionElement = document.getElementById('dark-mode-exclusion')

  if (themeMode === 'dark' && !exclusionElement) {
    rootElement.classList.add('darkMode')
  } else {
    rootElement.classList.remove('darkMode')
  }
}

function generateCssVars() {
  let cssVars
  const baseThemeVars = createThemeCssVars(baseTheme)
  const darkModeThemeVars = createThemeCssVars(darkModeTheme)

  return [baseThemeVars, darkModeThemeVars]

  function createThemeCssVars(theme) {
    cssVars = ''
    Object.keys(theme).forEach(key => {
      makeCssVariableString(key, [key], theme[key])
    })
    return cssVars
  }

  /* eslint-disable consistent-return */
  function makeCssVariableString(key, allKeys = [], value) {
    if (key === 'overrides') {
      // eslint-disable-line no-else-return
      return
    }
    if (typeof value === 'string') {
      // Regex matches hex colors
      if (value.match(/^#[a-fA-F0-9]{3,6}$/)) {
        cssVars += `--gameday-${allKeys.join('-')}: ${value}; `
        return
      }
      return
    }

    Object.keys(value).forEach(key => {
      makeCssVariableString(key, [...allKeys, key], value[key], cssVars)
    })

    return cssVars
  }
}

function rem(value) {
  return `${value / 16}rem`
}

export { generateCssVars, rem, toggleDarkMode }
export default theme
